import { Link } from "react-router-dom"
import { X, Home, MapPin, Calendar, MessageSquare, User, Settings, LogOut, Sun, Moon } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useTheme } from "@/components/theme-provider"
import { useAuth } from "@/contexts/AuthContext"
import { cn } from "@/lib/utils"

interface MobileNavProps {
  open: boolean
  onClose: () => void
}

export function MobileNav({ open, onClose }: MobileNavProps) {
  const { theme, setTheme } = useTheme()
  const { user, isAuthenticated, logout } = useAuth()

  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark")
  }

  const handleLogout = async () => {
    await logout()
    onClose()
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          "fixed inset-0 z-50 bg-black/50 backdrop-blur-sm transition-opacity md:hidden",
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={cn(
          "fixed inset-y-0 right-0 z-50 w-full max-w-xs bg-background shadow-sakura-lg transition-transform duration-300 ease-in-out md:hidden",
          open ? "translate-x-0" : "translate-x-full"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <span className="font-semibold text-lg">Menu</span>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation links */}
          <nav className="flex-1 overflow-y-auto p-4">
            <div className="space-y-1">
              <NavLink to="/" icon={Home} onClick={onClose}>
                Home
              </NavLink>
              <NavLink to="/explore" icon={MapPin} onClick={onClose}>
                Explore Tours
              </NavLink>

              {isAuthenticated && (
                <>
                  <NavLink to="/bookings" icon={Calendar} onClick={onClose}>
                    My Bookings
                  </NavLink>
                  <NavLink to="/messages" icon={MessageSquare} onClick={onClose}>
                    Messages
                  </NavLink>

                  {user?.role === "guide" && (
                    <NavLink to="/my-tours" icon={MapPin} onClick={onClose}>
                      My Tours
                    </NavLink>
                  )}

                  {user?.role === "admin" && (
                    <NavLink to="/admin" icon={Settings} onClick={onClose}>
                      Admin Panel
                    </NavLink>
                  )}
                </>
              )}
            </div>

            {/* Divider */}
            <div className="my-4 h-px bg-border" />

            {/* User section */}
            {isAuthenticated ? (
              <div className="space-y-1">
                <NavLink to="/profile" icon={User} onClick={onClose}>
                  Profile
                </NavLink>
                <button
                  onClick={handleLogout}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  <LogOut className="h-5 w-5" />
                  Log out
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <Button asChild className="w-full">
                  <Link to="/login" onClick={onClose}>
                    Log in
                  </Link>
                </Button>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/register" onClick={onClose}>
                    Sign up
                  </Link>
                </Button>
              </div>
            )}
          </nav>

          {/* Footer with theme toggle */}
          <div className="p-4 border-t">
            <Button
              variant="outline"
              className="w-full justify-start gap-3"
              onClick={toggleTheme}
            >
              {theme === "dark" ? (
                <>
                  <Sun className="h-5 w-5" />
                  Light mode
                </>
              ) : (
                <>
                  <Moon className="h-5 w-5" />
                  Dark mode
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}

interface NavLinkProps {
  to: string
  icon: React.ComponentType<{ className?: string }>
  children: React.ReactNode
  onClick: () => void
}

function NavLink({ to, icon: Icon, children, onClick }: NavLinkProps) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="flex items-center gap-3 rounded-lg px-3 py-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
    >
      <Icon className="h-5 w-5" />
      {children}
    </Link>
  )
}
